// AdminLlmCostPanel — the Cost ledger: what the AI providers charged us.
//
// Once a separate tab beside a wallet "Cost Ledger" (what was billed to user
// wallets); the owner merged the two on 29 Oct 2026 ("the same thing") and
// Token management went, so this is the one ledger. It is COST: what the
// providers actually charged, including every call no wallet could be charged
// for — candidate-facing interviews, queue workers, boot-time model probes,
// and calls that failed after burning input tokens.
//
// Routes used:
//   GET /api/admin/llm/spend?days=
//   GET /api/admin/llm/calls?operation=&model=&status=&unbilled=&limit=

import React, { useEffect, useMemo, useState } from "react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import api from "../../services/api";
import { toast } from "react-toastify";
import { Button, Card, Checkbox, EmptyState, Icon, Input, StatusPill, Table, useShellLayout } from "../../ds";
import { Page, PageHeader, StatRow, Stat, Section, Loading } from "../../components/page";

const RANGES = [
  { days: 7,  label: "7 days" },
  { days: 30, label: "30 days" },
  { days: 90, label: "90 days" },
];

const usd = (n) => `$${(n || 0).toFixed(n && Math.abs(n) < 1 ? 4 : 2)}`;

export default function AdminLlmCostPanel() {
  const layout = useShellLayout();
  const mobile = Boolean(layout && layout.mobile);
  const [days, setDays]         = useState(30);
  const [spend, setSpend]       = useState(null);
  const [calls, setCalls]       = useState([]);
  const [loading, setLoading]   = useState(true);

  // Table filters. `unbilledOnly` is the one that earns its place: it isolates
  // exactly the spend that never reaches the ledger.
  const [opFilter, setOpFilter]           = useState("");
  const [unbilledOnly, setUnbilledOnly]   = useState(false);
  const [errorsOnly, setErrorsOnly]       = useState(false);

  const load = async (opts = {}) => {
    const d  = opts.days ?? days;
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: "200" });
      if (opFilter)     params.set("operation", opFilter);
      if (unbilledOnly) params.set("unbilled", "1");
      if (errorsOnly)   params.set("status", "error");

      const [s, c] = await Promise.all([
        api.get(`/admin/llm/spend?days=${d}`),
        api.get(`/admin/llm/calls?${params.toString()}`),
      ]);
      setSpend(s.data || null);
      setCalls(c.data || []);
    } catch (e) {
      toast.error("Failed to load LLM spend");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const setRange = (d) => { setDays(d); load({ days: d }); };

  const errorCount = useMemo(
    () => (spend?.byOperation || []).reduce((a, r) => a + (r.errors || 0), 0),
    [spend]
  );
  const callCount = useMemo(
    () => (spend?.byOperation || []).reduce((a, r) => a + (r.calls || 0), 0),
    [spend]
  );

  // Share of provider spend we could not attribute to anyone. Surfaced as a
  // percentage because the absolute number means nothing without the base —
  // $40 unbilled is noise at $4,000 and an emergency at $50.
  const unbilledPct = spend && spend.totalUsd > 0
    ? (spend.unbilledUsd / spend.totalUsd) * 100
    : 0;

  const chartData = (spend?.byDay || []).map((d) => ({
    day: d._id.slice(5),          // MM-DD; the year is never in question here
    usd: Number((d.usd || 0).toFixed(4)),
    calls: d.calls || 0,
  }));


  const callColumns = [
    { key: "ts", label: "When", render: (r) => (
      <span style={{ fontSize: "var(--fs-body-sm)", color: "var(--text-muted)" }}>{new Date(r.ts).toLocaleString()}</span>
    ) },
    { key: "operation", label: "Operation", render: (r) => <span style={{ color: "var(--text-1)" }}>{r.operation}</span> },
    { key: "model", label: "Model", render: (r) => <span style={{ fontSize: "var(--fs-body-sm)" }}>{r.model}</span> },
    { key: "in", label: "In", render: (r) => <span className="num">{(r.inputTokens || 0).toLocaleString()}</span> },
    { key: "out", label: "Out", render: (r) => <span className="num">{(r.outputTokens || 0).toLocaleString()}</span> },
    { key: "usd", label: "USD", render: (r) => <span className="num">${(r.usdCost || 0).toFixed(5)}</span> },
    { key: "ms", label: "ms", render: (r) => <span className="num" style={{ color: "var(--text-muted)" }}>{r.latencyMs || 0}</span> },
    { key: "billed", label: "Billed", render: (r) => (
      r.billed
        ? <StatusPill tone="success" dot={false}>yes</StatusPill>
        : <StatusPill tone="warning" dot={false}>no</StatusPill>
    ) },
    { key: "status", label: "Status", render: (r) => (
      <StatusPill tone={r.status === "ok" ? "success" : "danger"}>{r.status}</StatusPill>
    ) },
  ];

  return (
    <Page>
      <PageHeader
        title="Cost ledger"
        subtitle="What the AI providers charged for Hiring at this school: every call, by what it was for and which model answered."
        actions={(
          <>
            {RANGES.map((r) => (
              <Button
                key={r.days}
                size="sm"
                variant={days === r.days ? "primary" : "secondary"}
                aria-pressed={days === r.days}
                onClick={() => setRange(r.days)}
              >
                {r.label}
              </Button>
            ))}
            <Button size="sm" variant="secondary" icon={<Icon name="arrow-clockwise" spin={loading} />} onClick={() => load()}>
              Reload
            </Button>
          </>
        )}
      />

      {loading && !spend && <Card><Loading /></Card>}

      {spend && (
        <>
          {/* The figures */}
          <StatRow>
            <Stat label={`Provider spend · ${days}d`} value={usd(spend.totalUsd)} />
            <Stat
              label="Unbilled (no user in scope)"
              value={usd(spend.unbilledUsd)}
              hint={`${unbilledPct.toFixed(1)}% of spend`}
              // Anything over a fifth of spend going unattributed is worth a
              // second look, so the card says so rather than leaving the
              // reader to work the ratio out.
              tone={unbilledPct > 20 ? "warning" : undefined}
            />
            {/* Prompt-cache hit rate. The interview prompts are ordered so the
                provider can cache their static half; this says whether that is
                actually landing. A rate near zero on a heavy interview week
                means something reordered a prompt and broke the prefix. */}
            <Stat
              label="Prompt cache hit rate"
              value={`${((spend.cacheHitRate || 0) * 100).toFixed(0)}%`}
              hint={`${(spend.cachedTokens || 0).toLocaleString()} of ${(spend.inputTokens || 0).toLocaleString()} input tokens`}
            />
            <Stat label="Calls" value={callCount.toLocaleString()} />
            <Stat
              label="Failed calls"
              value={errorCount.toLocaleString()}
              hint={errorCount ? "still cost input tokens" : undefined}
              tone={errorCount > 0 ? "warning" : undefined}
            />
          </StatRow>

          {/* Daily trend — a spike is a shape before it is a number, so this
              sits above the tables rather than below them. */}
          <Section title="Daily spend" subtitle={`last ${days} days`}>
            {chartData.length === 0 ? (
              <EmptyState icon={<Icon name="chart-bar" size={36} />} title="No calls" description="No calls recorded in this window." />
            ) : (
              <div style={{ width: "100%", height: 220 }}>
                <ResponsiveContainer>
                  <BarChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                    <XAxis dataKey="day" tick={{ fontSize: 11, fill: "var(--text-muted)" }} stroke="var(--border)" interval="preserveStartEnd" />
                    <YAxis tick={{ fontSize: 11, fill: "var(--text-muted)" }} stroke="var(--border)" width={60} tickFormatter={(v) => `$${v}`} />
                    <Tooltip
                      formatter={(v, name) => (name === "usd" ? [`$${Number(v).toFixed(4)}`, "Spend"] : [v, "Calls"])}
                      labelFormatter={(l) => `Day ${l}`}
                      cursor={{ fill: "var(--surface-2)" }}
                      contentStyle={{
                        fontSize: 12, borderRadius: "var(--radius-md)", border: "1px solid var(--border)",
                        background: "var(--surface-1)", color: "var(--text-1)",
                      }}
                    />
                    <Bar dataKey="usd" fill="var(--chart-1)" background={{ fill: "var(--chart-track)" }} radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </Section>

          {/* Where it went. Operation first: it is the cut that names a feature
              to go and fix, which is what a spike actually needs. */}
          <div style={{ display: "grid", gridTemplateColumns: mobile ? "1fr" : "repeat(auto-fit,minmax(320px,1fr))", gap: mobile ? 16 : 24 }}>
            <SpendTable
              title="By operation"
              rows={spend.byOperation}
              total={spend.totalUsd}
              onPick={(op) => { setOpFilter(op); setUnbilledOnly(false); setErrorsOnly(false); }}
            />
            <SpendTable
              title="By model"
              rows={spend.byModel}
              total={spend.totalUsd}
            />
          </div>

          {/* Recent calls */}
          <Section title="Recent calls" subtitle={`${calls.length} shown`} padding="20px 0 8px">
            <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap", padding: "0 20px 16px" }}>
              <div style={{ width: 200 }}>
                <Input
                  value={opFilter}
                  onChange={(e) => setOpFilter(e.target.value)}
                  placeholder="operation"
                />
              </div>
              <Checkbox checked={unbilledOnly} onChange={setUnbilledOnly} label="Unbilled only" />
              <Checkbox checked={errorsOnly} onChange={setErrorsOnly} label="Errors only" />
              <Button size="sm" onClick={() => load()}>Apply</Button>
            </div>

            {loading && <Loading />}

            {!loading && calls.length === 0 && (
              <EmptyState icon={<Icon name="list" size={36} />} title="Nothing here" description="No calls match." />
            )}

            {!loading && calls.length > 0 && (
              <Table columns={callColumns} rows={calls} getRowId={(r) => r._id} stacked={mobile} />
            )}
          </Section>
        </>
      )}
    </Page>
  );
}

/* Ranked spend, with the bar scaled against the window total rather than
   against the largest row — so one dominant operation reads as dominant
   instead of every list looking the same shape. */
function SpendTable({ title, rows, total, onPick }) {
  const list = Array.isArray(rows) ? rows.slice(0, 10) : [];
  const base = total || 1;
  return (
    <Section title={title} subtitle={onPick ? "pick one to filter the calls" : undefined}>
      {list.length === 0 && <div style={{ color: "var(--text-muted)", fontSize: "var(--fs-body-sm)" }}>No data</div>}
      <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gap: 12 }}>
        {list.map((r, i) => {
          const pct = ((r.usd || 0) / base) * 100;
          return (
            <li key={i}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8, marginBottom: 4, fontSize: "var(--fs-body-sm)" }}>
                <button
                  type="button"
                  disabled={!onPick}
                  onClick={() => onPick && onPick(r._id)}
                  title={onPick ? "Filter recent calls by this" : undefined}
                  style={{
                    appearance: "none", background: "none", border: "none", padding: 0, font: "inherit", textAlign: "left",
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0,
                    color: onPick ? "var(--brand-primary-ink)" : "var(--text-1)", cursor: onPick ? "pointer" : "default",
                  }}
                >
                  {String(r._id || "—")}
                </button>
                <span className="num" style={{ color: "var(--text-2)", flex: "none" }}>
                  ${(r.usd || 0).toFixed(4)}
                  <span style={{ color: "var(--text-muted)", marginLeft: 6 }}>{pct.toFixed(0)}%</span>
                </span>
              </div>
              <div style={{ width: "100%", height: 6, background: "var(--chart-track)", borderRadius: "var(--radius-pill)" }}>
                <div style={{ height: 6, background: "var(--chart-1)", borderRadius: "var(--radius-pill)", width: `${Math.min(pct, 100)}%` }} />
              </div>
              <div style={{ fontSize: "var(--fs-caption)", color: "var(--text-muted)", marginTop: 4 }}>
                {(r.calls || 0).toLocaleString()} calls
                {r.errors > 0 && <span style={{ color: "var(--status-warning-text)" }}> · {r.errors} failed</span>}
              </div>
            </li>
          );
        })}
      </ul>
    </Section>
  );
}
